/**
 * capabilities.ts
 * Frontend capability matching — mirrors matchCapability from @suite/kernel.
 *
 * The frontend CANNOT import from @suite/kernel (boundary: apps/frontend only imports
 * @suite/contracts-*). This module keeps the capability logic in lockstep with the
 * kernel implementation via shared test vectors.
 *
 * Phase 1a: stub required by filterNavForUser.
 * Phase 1b (FS-23): full TDD test suite (FS-23-A through FS-23-E) will be added.
 *
 * Glob semantics (must match kernel matchCapability exactly — AZ-2):
 *   '*'        — matches any non-empty required capability
 *   '<ns>:*'   — matches any capability starting with '<ns>:'
 *   '<exact>'  — exact string equality
 *   empty caps — always false (fail-closed)
 *   empty req  — always false (no capability should match "nothing")
 *
 * Spec: FS-23 / AZ-2 / design §9 Phase 1a
 */

/**
 * Returns true if the user's held capabilities satisfy the required capability.
 *
 * Mirrors kernel matchCapability glob semantics exactly.
 * Used by filterNavForUser to gate individual nav items.
 *
 * @param held       Array of capability strings the user holds (from JWT)
 * @param required   The capability required to see a nav item
 */
export function hasCapability(held: readonly string[], required: string): boolean {
  if (!required || held.length === 0) return false;

  for (const cap of held) {
    if (cap === '*') return true;
    if (cap === required) return true;
    // length > 2 rejects a degenerate ':*' (empty namespace) — lockstep with the
    // kernel matchCapability guard so the mirror stays exact (AZ-2).
    if (cap.length > 2 && cap.endsWith(':*') && required.startsWith(cap.slice(0, -1))) return true;
  }

  return false;
}

/**
 * 2026-08-31-requi-aprobacion-oc-por-monto (design.md D7) — any-of gate for
 * routes/nav items that MUST be reachable by more than one capability (e.g.
 * `/compras/aprobaciones`, which a tier-1-only `requi.comprador` and a
 * tier-2-only `requi.aprobador` must both reach, even though neither one
 * holds `requi.caso.aprobar`). A plain `string` required value is a
 * single-element check, byte-identical to calling `hasCapability` directly —
 * every existing call site that only ever passed a string keeps working
 * unchanged.
 */
export function hasAnyCapability(held: readonly string[], required: string | readonly string[]): boolean {
  const list = Array.isArray(required) ? required : [required as string];
  return list.some((req) => hasCapability(held, req));
}
